"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ShieldCheck, Clock, TrendingUp, SlidersHorizontal as Sliders, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const benefits = [{
  icon: ShieldCheck,
  title: "Maior longevidade dos equipamentos",
  description: "Materiais de alta resistência química e mecânica reduzem o desgaste precoce dos bicos e acessórios em ciclos exigentes.",
  metrics: "+40% vida útil operacional",
  points: ["Ligas especiais e cerâmica técnica de alta dureza", "Menor necessidade de substituições corretivas frequentes", "Resistência superior à abrasão e corrosão de fluidos"]
}, {
  icon: Clock,
  title: "Redução comprovada do tempo de paragem",
  description: "Sistemas concebidos para manutenção rápida e diagnósticos preventivos sem interrupção prolongada da linha de produção.",
  metrics: "-65% tempo em intervenções técnicas",
  points: ["Bicos de encaixe rápido com alinhamento automático QuickJet", "Inspeção e limpeza simplificada sem recalibração manual", "Planeamento preventivo orientado por histórico operacional"]
}, {
  icon: TrendingUp,
  title: "Otimização contínua de desempenho",
  description: "Atomização precisa e padrões de pulverização uniformes que garantem a cobertura exata sem desperdício de produto.",
  metrics: "99.2% precisão volumétrica",
  points: ["Controlo rigoroso do tamanho de gota (DSD)", "Distribuição uniforme de caudal ao longo da linha", "Monitorização avançada de caudal e pressão em tempo real"]
}, {
  icon: Sliders,
  title: "Eficiência de recursos e sustentabilidade",
  description: "Aplicação exata de produtos químicos, água e energia com ganhos ambientais diretos e controlo de custos operacionais.",
  metrics: "Até -30% no consumo de matéria-prima",
  points: ["Redução imediata de névoas e perdas por dispersão", "Sistemas de recirculação e dosagem estequiométrica", "Cumprimento integral das normas ambientais industriais"]
}];
export default function Benefits() {
  const containerRef = useRef<HTMLElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-header", {
        y: 28
      });
      gsap.to(".reveal-header", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".reveal-card", {
        y: 36
      });
      gsap.to(".reveal-card", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".benefits-grid",
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".reveal-cta", {
        y: 24
      });
      gsap.to(".reveal-cta", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".reveal-cta",
          start: "top 90%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".reveal-header", ".reveal-card", ".reveal-cta"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="technicalbenefits" ref={containerRef} data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="reveal-header mx-auto max-w-3xl text-center mb-16 lg:mb-20" style={{
        opacity: 0
      }}>
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider py-1 px-3">
            Engenharia de Pulverização
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Vantagens técnicas para operações contínuas
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A tecnologia da Spraying Systems Co. combinada com o suporte técnico local da Eurocontrol 
            assegura rendimento máximo, menos manutenção corretiva e total consistência produtiva.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="benefits-grid grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {benefits.map((item, index) => {
          const Icon = item.icon;
          return <div key={item.title} data-index={index} className="reveal-card flex" style={{
            opacity: 0
          }}>
                <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 rounded-xl">
                  <CardHeader className="space-y-4 pb-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent text-primary">
                        <Icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <Badge variant="secondary" className="font-mono text-xs font-medium px-2.5 py-1">
                        {item.metrics}
                      </Badge>
                    </div>
                    <CardTitle className="font-default text-xl font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2">
                    <ul className="space-y-2.5 border-t border-border pt-4">
                      {item.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2.5 text-sm text-foreground/90">
                          <CheckCircle2 className="h-4 w-4 text-secondary mt-0.5 shrink-0" aria-hidden="true" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        {/* CTA Bar */}
        <div className="reveal-cta mt-16 lg:mt-20 rounded-2xl bg-muted/60 border border-border p-8 lg:p-10 flex flex-col md:flex-row items-center justify-between gap-6" style={{
        opacity: 0
      }}>
          <div className="max-w-2xl text-center md:text-left">
            <h3 className="font-default text-xl sm:text-2xl font-bold text-foreground">
              Pretende auditar o rendimento do seu circuito de pulverização?
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              Os nossos engenheiros realizam diagnósticos no terreno com medição de caudal, pressão e cobertura.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/TechnicalSolutionsPage">
                Explorar Soluções
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/ContactsPage">
                Consulte-nos
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}